'use client';

import 'dayjs/locale/ko';

import Button from '@enlighten/design-system/dist/Button';
import ButtonGroup from '@enlighten/design-system/dist/ButtonGroup';
import Grid from '@enlighten/design-system/dist/Grid';
import Select from '@enlighten/design-system/dist/Select';
import { useTheme } from '@enlighten/design-system/dist/styles';
import Typography from '@enlighten/design-system/dist/Typography';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import dayjs from 'dayjs';
import ECharts from 'echarts-for-react';
import { useState } from 'react';

import AnimatedNumberTypography from '@/components/pages/common/AnimatedNumberTypography';
import { useMarketContext } from '@/contexts/pages/market/MarketProvider';
import { palette } from '@/styles/theme';

const areaCodes = [
	{ label: '통합', value: '0' },
	{ label: '육지', value: '1' },
	{ label: '제주', value: '9' },
];

function getDecimal(value: number) {
	return Math.floor(value);
}

function getFloatingNumber(value: number) {
	return value - Math.floor(value);
}

function getFluctuationSymbol(value: number) {
	return value > 0 ? '+' : value < 0 ? '-' : '';
}

function getFluctuationColor(value: number) {
	return value > 0 ? palette.error.main : value < 0 ? palette.success.main : palette.grey['900'];
}

dayjs.locale('ko');

export default function Trend() {
	const [weight, setWeight] = useState<'0.7' | '1.0' | '1.2' | '1.5'>('1.0');

	const theme = useTheme();

	const { areaCode, onChangeAreaCode, data } = useMarketContext();

	if (!data) {
		return <></>;
	}

	const smp = data[areaCode].smpLatest?.average || 0;
	const rec = data[areaCode].recLatest?.averagePrice;
	const smpRec = (data[areaCode].smpLatest?.average || 0) + ((data[areaCode].recLatest?.averagePrice || 0) / 1000) * +weight;

	return (
		<Grid container gap="22px">
			<Grid container width="100%">
				<Typography fontSize={24} fontWeight={700}>
					가격동향
				</Typography>
				<Grid
					container
					flex={1}
					alignItems="center"
					sx={{
						flexDirection: { xs: 'column', md: 'row' },
						justifyContent: 'space-between',
						alignItems: { xs: 'flex-end', md: 'center' },
						gap: { xs: 1, md: 0 },
					}}
				>
					{/* REC 가중치 선택 */}
					<Select
						value={weight}
						onChange={(e) => {
							setWeight(e.target.value as typeof weight);
						}}
						IconComponent={ExpandMoreOutlined}
						items={['0.7', '1.0', '1.2', '1.5'].map((value) => ({
							title: `REC 가중치 ${value}`,
							value,
							sx: {
								fontSize: 16,
								padding: '5px 12px',
								'&.Mui-selected': {
									bgcolor: '#F4E6FF',
									':hover': {
										bgcolor: '#F4E6FF',
									},
								},
							},
						}))}
						sx={(theme) => ({
							fontSize: 16,
							fontFamily: theme.typography.fontFamily,
							color: 'grey.900',
							marginLeft: 5,
							'.MuiSelect-select': {
								bgcolor: 'grey.100',
								border: 'none',
								padding: '2px 0 2px 16px',
								':hover': {
									border: 'none',
								},
							},
						})}
					/>
					{/* 통합 / 육지 / 제주 선택 */}
					<ButtonGroup
						variant="outlined"
						size="small"
						sx={(theme) => ({
							borderRadius: '6px',
							'.MuiButton-root': {
								fontSize: 14,
								fontWeight: 400,
								fontFamily: theme.typography.fontFamily,
								color: 'grey.900',
								height: 28,
								paddingX: '7px',
							},
						})}
					>
						{areaCodes.map(({ label, value }, i) => (
							<Button
								key={i}
								onClick={() => {
									onChangeAreaCode(value as typeof areaCode);
								}}
								sx={(theme) => ({
									borderColor: areaCode === value ? theme.palette.primary.main : undefined,
									zIndex: 99 - i,
									'&.MuiButton-root': {
										color: areaCode === value ? theme.palette.primary.main : undefined,
										borderRightColor: areaCode === value ? theme.palette.primary.main : undefined,
										borderTopLeftRadius: i === 0 ? '6px' : 0,
										borderBottomLeftRadius: i === 0 ? '6px' : 0,
										borderTopRightRadius: i === areaCodes.length - 1 ? '6px' : 0,
										borderBottomRightRadius: i === areaCodes.length - 1 ? '6px' : 0,
										':hover': {
											color: theme.palette.primary.main,
										},
									},
								})}
							>
								{label}
							</Button>
						))}
					</ButtonGroup>
				</Grid>
			</Grid>
			{/* SMP / REC / SMP + REC * WEIGHT */}
			<Grid
				container
				width="100%"
				sx={(theme) => ({
					gap: 5,
					'.num': {
						fontSize: 32,
						lineHeight: 1.23,
					},
					[theme.breakpoints.down('md')]: {
						flexDirection: 'column',
						gap: 3,
					},
				})}
			>
				{/* SMP */}
				<Grid flex={1}>
					<Grid container alignItems="center">
						<Typography fontSize={16}>SMP</Typography>
						<Typography color="grey.700" marginLeft={1}></Typography>
						{dayjs(data[areaCode].smpLatest?.date).format('YYYY.MM.DD (ddd)')}
					</Grid>
					<Grid container alignItems="flex-end" marginTop={1}>
						<Grid display="flex">
							<AnimatedNumberTypography className="num" includesComma value={getDecimal(smp)} />
							<Typography fontSize={32}>.</Typography>
							<AnimatedNumberTypography className="num" includesComma value={Math.round(getFloatingNumber(smp) * 100)} format={(value) => `${value}`.padStart(2, '0')} />
						</Grid>
						<Typography lineHeight={2} marginLeft="6px">
							원/kWh
						</Typography>
						<Typography lineHeight={2} marginLeft="4px" color={getFluctuationColor(data[areaCode].smpLatest?.fluctuationPrice || 0)}>
							{getFluctuationSymbol(data[areaCode].smpLatest?.fluctuationPrice || 0)}
							{Math.abs(data[areaCode].smpLatest?.fluctuationPrice || 0).toFixed(2)}
						</Typography>
					</Grid>
				</Grid>
				{/* REC 현물 */}
				<Grid flex={1}>
					<Grid container alignItems="center">
						<Typography fontSize={16}>REC 현물</Typography>
						<Typography color="grey.700" marginLeft={1}>
							{dayjs(data[areaCode].recLatest?.date).format('YYYY.MM.DD (ddd)')}
						</Typography>
					</Grid>
					<Grid container alignItems="flex-end" marginTop={1}>
						<AnimatedNumberTypography className="num" includesComma value={rec || 0} />
						<Typography lineHeight={2} marginLeft="6px">
							원/REC
						</Typography>
						<Typography lineHeight={2} marginLeft="4px" color={getFluctuationColor(data[areaCode].recLatest?.fluctuationPrice || 0)}>
							{getFluctuationSymbol(data[areaCode].recLatest?.fluctuationPrice || 0)}
							{Math.abs(data[areaCode].recLatest?.fluctuationPrice || 0).toLocaleString()}
						</Typography>
					</Grid>
				</Grid>
				{/* SMP + (REC × WEIGHT) */}
				<Grid flex={1}>
					<Grid container alignItems="center">
						<Typography fontSize={16}>
							SMP + (REC {'\u00d7'} {weight})
						</Typography>
					</Grid>
					<Grid container alignItems="flex-end" marginTop={1}>
						<Grid display="flex">
							<AnimatedNumberTypography className="num" includesComma value={getDecimal(smpRec)} />
							<Typography fontSize={32}>.</Typography>
							<AnimatedNumberTypography className="num" includesComma value={Math.round(getFloatingNumber(smpRec) * 100)} format={(value) => `${value}`.padStart(2, '0')} />
						</Grid>
						<Typography lineHeight={2} marginLeft="6px">
							원/kWh
						</Typography>
					</Grid>
				</Grid>
			</Grid>
			<Grid container width="100%">
				<ECharts
					// notMerge
					// lazyUpdate
					style={{ width: '100%', height: 310 }}
					option={{
						textStyle: {
							fontFamily: theme.typography.fontFamily,
						},
						legend: {
							top: 0,
							left: 10,
							bottom: 'auto',
							itemGap: 20,
							textStyle: {
								color: theme.palette.grey['900'],
								fontFamily: theme.typography.fontFamily,
							},
						},
						xAxis: [
							{
								type: 'category',
								boundaryGap: false,
								data: data[areaCode].smpMonthly.map((item) => `${item.year}.${`${item.month}`.padStart(2, '0')}`),
							},
						],
						yAxis: [
							{
								type: 'value',
							},
						],
						grid: {
							top: 40,
							right: 30,
							bottom: 60,
							left: 0,
							containLabel: true,
						},
						dataZoom: [
							{
								type: 'slider',
								start: 0,
								end: 100,
							},
						],
						tooltip: {
							trigger: 'axis',
							confine: true,
							textStyle: {
								fontFamily: theme.typography.fontFamily,
							},
						},
						series: [
							{
								type: 'line',
								smooth: true,
								areaStyle: {
									opacity: 0.07,
								},
								name: 'SMP',
								showSymbol: false,
								data: data[areaCode].smpMonthly.map(({ price }) => Math.round(price * 100) / 100),
								itemStyle: {
									color: theme.palette.primary.main,
								},
							},
							{
								type: 'line',
								smooth: true,
								areaStyle: {
									opacity: 0.07,
								},
								name: 'REC',
								showSymbol: false,
								data: data[areaCode].recMonthly.map(({ price }) => Math.round(price) / 1000),
								itemStyle: {
									color: '#08B468',
								},
							},
							{
								type: 'line',
								smooth: true,
								areaStyle: {
									opacity: 0.07,
								},
								name: 'SMP + REC',
								showSymbol: false,
								data: data[areaCode].smpMonthly.map(({ price: smpPrice }, i) => Math.round((smpPrice + ((data[areaCode].recMonthly[i]?.price || 0) / 1000) * +weight) * 100) / 100),
								itemStyle: {
									color: '#E8A328',
								},
							},
						],
					}}
				/>
			</Grid>
		</Grid>
	);
}
